import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { ONBOARDING_TOKEN_STORAGE_KEY } from "@/constants";
import { personifyQueryOptions } from "@/domains/profile/factories/personify-query-options";
import { LOGIN_ERRORS } from "@/domains/profile/models/constants";
import { env } from "@/env";

/**
 * Orchestrates the OIDC patient onboarding flow on the profiling page.
 *
 * Two-step contract:
 *  1. Exchange the JWS onboarding token (from sessionStorage) for the opaque
 *     `linkToken` that prefills into the Formsort embed.
 *  2. After Formsort finalizes, poll `/onboarding/status` until the backend
 *     webhook has processed the submission, then hard-navigate to the silent
 *     OIDC sign-in round-trip that mints a session JWT.
 *
 * Failure paths clear the cached token and route to `/login?error=…` so the
 * user can re-initiate from a clean state instead of looping on a stale
 * sessionStorage entry.
 *
 * Non-OIDC patients hit the same hook; the token is null, both queries are
 * disabled, and the hook becomes effectively a no-op.
 */
export const usePersonify = () => {
  const navigate = useNavigate();

  // Tab-scoped. Read once at mount and stick with that value for the lifetime
  // of this hook instance; clearing happens via the explicit side-effect paths
  // below, never via re-reading sessionStorage.
  const onboardingTokenRef = useRef<string | null>(
    sessionStorage.getItem(ONBOARDING_TOKEN_STORAGE_KEY),
  );
  const onboardingToken = onboardingTokenRef.current;

  const [awaitingWebhook, setAwaitingWebhook] = useState(false);

  // Step 1: JWS → linkToken.
  const exchange = useQuery({
    ...personifyQueryOptions.onboardingExchange(onboardingToken ?? ""),
    enabled: !!onboardingToken,
    retry: false,
    staleTime: Infinity,
  });

  // Step 2: poll readiness once Formsort has finalized.
  const status = useQuery({
    ...personifyQueryOptions.onboardingStatus(onboardingToken ?? ""),
    enabled: awaitingWebhook && !!onboardingToken,
    refetchInterval: (query) => {
      return query.state.data?.ready ? false : 500;
    },
    retry: false,
    staleTime: 0,
  });

  // Failure: exchange couldn't resolve the token. Clear and bounce to login.
  useEffect(() => {
    if (!exchange.isError) {
      return;
    }
    sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
    void navigate({
      to: "/login",
      search: { error: LOGIN_ERRORS.OIDC_ONBOARDING_EXCHANGE_FAILED },
    });
  }, [exchange.isError, navigate]);

  // Failure: polling failed (network, 410, etc.). Same recovery path.
  useEffect(() => {
    if (!status.isError) {
      return;
    }
    sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
    void navigate({
      to: "/login",
      search: { error: LOGIN_ERRORS.OIDC_ONBOARDING_STATUS_FAILED },
    });
  }, [status.isError, navigate]);

  // Success: webhook processed. Clear the token and start the silent OIDC
  // round-trip; the callback will see user_id set and issue a session JWT.
  useEffect(() => {
    if (!status.data?.ready) {
      return;
    }
    sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
    window.location.href = `${env.VITE_API_URL}/webapp/auth/oidc/start?trigger=profiling_complete`;
  }, [status.data?.ready]);

  const linkToken = exchange.data?.linkToken;

  return {
    onboardingToken,
    queryParams: linkToken ? ([["link_token", linkToken]] as Array<[string, string]>) : undefined,
    isExchangeLoading: exchange.isLoading,
    awaitingWebhook,
    setAwaitingWebhook,
  };
};
